import type { Team, TeamCreatePayload } from "@sgdf/ui-library";
import type { TeamIn, TeamPatch } from "@sgdf/client-v2";
import {
  createTeam,
  destroyTeam,
  listTeams,
  patchTeam,
  retrieveTeam,
  setTeamParticipants,
} from "@sgdf/client-v2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { unref } from "vue";

import { apiCall } from "@/core/utils/api";
import { useStaleParticipantsErrorHandler } from "./participant";
import { useMeetingId, useTeamId } from "./routes";
import { usePermission } from "./permission";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getInvalidatePredicate = (meetingId: string) => (query: any) => {
  return (
    query.queryKey[0] === "meetings" &&
    query.queryKey[1] === meetingId &&
    typeof query.queryKey[2] === "string" &&
    ["teams", "participants"].includes(query.queryKey[2])
  );
};

export function useCreateTeamMutation() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: { team: TeamCreatePayload }) => {
      const body: TeamIn = {
        label: options.team.label,
        color: options.team.color ?? "#00395C",
        // "" (FK non sélectionnée) → null, sinon ninja rejette category_id
        // (UUID | null) avec une 422.
        category_id: options.team.category_id || null,
      };
      return apiCall(
        createTeam({
          path: { meeting_id: unref(meetingId)! },
          body,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: getInvalidatePredicate(unref(meetingId)!),
      });
    },
  });
}

export function useUpdateTeamMutation() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: { team: Partial<Team> & { id: string } }) => {
      const body: TeamPatch = {
        label: options.team.label,
        color: options.team.color,
        // PATCH : "" → null (déselection), `undefined` préservé pour ne pas
        // effacer un champ non fourni.
        category_id:
          options.team.category_id === "" ? null : options.team.category_id,
      };
      return apiCall(
        patchTeam({
          path: { meeting_id: unref(meetingId)!, team_id: options.team.id },
          body,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: getInvalidatePredicate(unref(meetingId)!),
      });
    },
  });
}

export function useDeleteTeamMutation() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: { id: string }) =>
      apiCall(
        destroyTeam({
          path: { meeting_id: unref(meetingId)!, team_id: options.id },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: getInvalidatePredicate(unref(meetingId)!),
      });
    },
  });
}

export function useTeam() {
  const meetingId = useMeetingId();
  const teamId = useTeamId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "teams", teamId],
    queryFn: () =>
      apiCall(
        retrieveTeam({
          path: { meeting_id: unref(meetingId)!, team_id: teamId! },
        }),
      ),
    enabled: () => {
      const id = unref(meetingId);
      return id != null && teamId != null && hasPermission.value("view_team", id);
    },
  });
}

export const useTeams = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "teams"] as const,
    queryFn: () =>
      apiCall(listTeams({ path: { meeting_id: unref(meetingId)! } })),
    enabled: () => {
      const id = unref(meetingId);
      return id != null && hasPermission.value("view_team", id);
    },
    staleTime: Infinity,
  });
};

export function useUpdateTeamParticipantMutation() {
  const meetingId = useMeetingId();
  const invalidatePredicate = getInvalidatePredicate(unref(meetingId)!);
  const onStaleParticipantsError =
    useStaleParticipantsErrorHandler(invalidatePredicate);

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options: { teamId: string; participants: string[] }) =>
      apiCall(
        setTeamParticipants({
          path: { meeting_id: unref(meetingId)!, team_id: options.teamId },
          body: { participants: options.participants },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: invalidatePredicate });
    },
    onError: onStaleParticipantsError,
  });
}
